// src/Pages/Inventory/RawInventoryList.jsx
import React, { useEffect, useState, useCallback } from "react";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { showCurrencySymbol, showToast } from "../../helper/generalHelper";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";

// --- Helper Functions & Constants ---
const unitOptions = ["ml", "liter", "mg", "g", "kg", "pieces","dozen"];
const safeParse = (val) => {
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
};

// --- UI Components ---
const Card = ({ children }) => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden">
    {children}
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 transition-opacity">
    <div className="bg-white rounded-xl w-full max-w-2xl p-6 shadow-2xl">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">{title}</h2>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-800">
          ✕
        </button>
      </div>
      {children}
    </div>
  </div>
);

const ConfirmationDialog = ({ title, message, onConfirm, onCancel }) => (
  <Modal title={title} onClose={onCancel}>
    <p className="text-gray-600 mb-6">{message}</p>
    <div className="flex justify-end gap-3">
      <button
        onClick={onCancel}
        className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg"
      >
        Confirm Delete
      </button>
    </div>
  </Modal>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:border-primary focus:ring-primary"
  />
);

const Select = (props) => (
  <select
    {...props}
    className="w-full border-gray-300 rounded-lg shadow-sm text-sm focus:border-primary focus:ring-primary"
  />
);

const InventoryFormRow = ({
  item,
  index,
  onChange,
  onRemove,
  showRemoveButton = false,
}) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 items-center p-2 rounded-lg transition-colors hover:bg-gray-50 border-b sm:border-b-0 pb-4 sm:pb-2">
    <div className="col-span-1 sm:col-span-2 md:col-span-2 flex flex-col">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Item Name
      </label>
      <Input
        type="text"
        placeholder="Item Name"
        value={item.itemName}
        onChange={(e) => onChange(index, "itemName", e.target.value)}
        required
      />
    </div>

    <div className="flex flex-col col-span-1">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Measurement
      </label>
      <Select
        value={item.measurement}
        onChange={(e) => onChange(index, "measurement", e.target.value)}
        required
      >
        <option value="">Select Unit</option>
        {unitOptions.map((unit) => (
          <option key={unit} value={unit}>
            {unit}
          </option>
        ))}
      </Select>
    </div>

    <div className="flex flex-col col-span-1">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Stock
      </label>
      <Input
        type="number"
        placeholder="Stock"
        value={item.stock}
        onChange={(e) => onChange(index, "stock", e.target.value)}
      />
    </div>

    <div className="flex flex-col col-span-1">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Unit Price
      </label>
      <Input
        type="number"
        placeholder="Unit Price"
        value={item.unitPrice}
        onChange={(e) => onChange(index, "unitPrice", e.target.value)}
        required
      />
    </div>

    <div className="flex flex-col col-span-1">
      <label className="block text-sm font-medium text-gray-600 mb-1">
        Reorder Level
      </label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          placeholder="Reorder"
          value={item.reorderLevel}
          onChange={(e) => onChange(index, "reorderLevel", e.target.value)}
        />
        {showRemoveButton && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-primary hover:text-red-500 transition-colors"
          >
            <FaTrash size={20} />
          </button>
        )}
      </div>
    </div>
  </div>
);

// --- Add Bulk Modal (same as before) ---
const AddBulkInventoryModal = ({ onClose, onSubmit }) => {
  const [newItems, setNewItems] = useState([
    {
      itemName: "",
      measurement: "",
      stock: "",
      unitPrice: "",
      reorderLevel: "",
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (index, field, value) => {
    setNewItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };
  const handleAddRow = () =>
    setNewItems([
      ...newItems,
      {
        itemName: "",
        measurement: "",
        stock: "",
        unitPrice: "",
        reorderLevel: "",
      },
    ]);
  const handleRemoveRow = (index) =>
    newItems.length > 1 &&
    setNewItems((prev) => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const payload = newItems
      .filter(
        (item) =>
          item.itemName?.trim() && item.measurement && item.unitPrice !== ""
      )
      .map((item) => ({
        ...item,
        stock: safeParse(item.stock),
        unitPrice: safeParse(item.unitPrice),
        reorderLevel: safeParse(item.reorderLevel),
      }));

    if (!payload.length) {
      toast.error(
        "Please add at least one valid item with a name, unit, and price."
      );
      setIsSubmitting(false);
      return;
    }
    await onSubmit(payload);
    setIsSubmitting(false);
  };

  return (
    <Modal title="Add New Inventory Items" onClose={onClose}>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        {newItems.map((item, index) => (
          <InventoryFormRow
            key={index}
            item={item}
            index={index}
            onChange={handleChange}
            onRemove={handleRemoveRow}
            showRemoveButton={newItems.length > 1}
          />
        ))}
      </div>
      <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-3">
        <button
          onClick={handleAddRow}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg w-full sm:w-auto"
        >
          + Add Another Row
        </button>
        <div className="flex gap-3 w-full sm:w-auto">
          <button
            onClick={onClose}
            className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg w-full"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-primary hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg w-full disabled:bg-blue-300"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// --- Edit Modal ---
const InventoryFormModal = ({ item, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    ...item,
    stock: item.stock?.toString() || "0",
    unitPrice: item.unitPrice?.toString() || "0",
    reorderLevel: item.reorderLevel?.toString() || "0",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (_, field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (
      !formData.itemName?.trim() ||
      !formData.measurement ||
      !formData.unitPrice
    ) {
      toast.error("Item Name, Measurement, and Unit Price are required.");
      return;
    }
    setIsSubmitting(true);
    const payload = {
      ...formData,
      stock: safeParse(formData.stock),
      unitPrice: safeParse(formData.unitPrice),
      reorderLevel: safeParse(formData.reorderLevel),
    };
    await onSubmit(payload);
    setIsSubmitting(false);
  };

  return (
    <Modal title={`Edit - ${item.itemName}`} onClose={onClose}>
      <InventoryFormRow item={formData} onChange={handleChange} index={0} />
      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={onClose}
          className="border border-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-primary hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg disabled:bg-blue-300"
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
};

// --- MAIN COMPONENT ---
const RawInventoryList = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]); // 🔹 NEW
  const [currentPage, setCurrentPage] = useState(1); // 🔹 NEW
  const itemsPerPage = 10; // 🔹 NEW

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";

  const fetchRawMaterials = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`${apiPrefix}/inventory/get`);
      const inventory = res.data?.inventory || [];
      setItems(inventory);

      // ✅ Reset pagination if current page > total pages
      const totalPages = Math.ceil(inventory.length / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      } else if (totalPages === 0) {
        setCurrentPage(1);
      }
    } catch (err) {
      toast.error("Failed to fetch raw materials.");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchRawMaterials();
  }, [fetchRawMaterials]);

  const handleAddNewItems = async (newItemsPayload) => {
    try {
      const res = await api.post(`${apiPrefix}/inventory/new`, newItemsPayload);
      setIsAddModalOpen(false);
      showToast(res);
      fetchRawMaterials();
    } catch (error) {
      toast.error(
        `Error adding inventory: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleUpdateItem = async (updatedItem) => {
    try {
      const res = await api.post(`${apiPrefix}/inventory/update`, updatedItem);
      setEditingItem(null);
      showToast(res);
      fetchRawMaterials();
    } catch (error) {
      toast.error(
        `Error updating inventory: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  // 🔹 MULTI DELETE HANDLER
  const handleDeleteSelected = async () => {
    if (!selectedItems.length) return toast.warn("No items selected.");
    if (!window.confirm(`Delete ${selectedItems.length} selected items?`))
      return;

    try {
      const res = await api.post(`${apiPrefix}/inventory/delete-multiple`, {
        ids: selectedItems,
      });
      showToast(res);
      setSelectedItems([]);

      // ✅ Optional: move one page back if all items on page deleted
      if (currentItems.length === selectedItems.length && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

      fetchRawMaterials();
    } catch (error) {
      toast.error(
        `Error deleting items: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  // 🔹 PAGINATION LOGIC
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = items.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(items.length / itemsPerPage);

  // 🔹 SELECTION LOGIC
  const toggleSelectAll = () => {
    if (selectedItems.length === currentItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentItems.map((i) => i.id));
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col mb-6 sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Raw Material Inventory
          </h1>
          <p className="text-sm text-gray-500">
            Manage your stock of raw ingredients.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 bg-primary hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm"
          >
            <FaPlus size={16} /> Add New
          </button>
          <button
            onClick={handleDeleteSelected}
            disabled={!selectedItems.length}
            className={`flex items-center gap-2 ${
              selectedItems.length
                ? "bg-red-600 hover:bg-red-700"
                : "bg-gray-300"
            } text-white font-semibold py-2 px-4 rounded-lg shadow-sm`}
          >
            <FaTrash size={16} />
            Delete {selectedItems.length ? `(${selectedItems.length})` : ""}
          </button>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3">
                  <input
                    type="checkbox"
                    onChange={toggleSelectAll}
                    checked={
                      currentItems.length > 0 &&
                      selectedItems.length === currentItems.length
                    }
                  />
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Item Name
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Measurement
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Stock
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Unit Price
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Reorder Level
                </th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-500">
                    Loading inventory...
                  </td>
                </tr>
              ) : currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${
                      selectedItems.includes(item.id) ? "bg-blue-50" : ""
                    }`}
                  >
                    <td className="px-6 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleSelectItem(item.id)}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">
                          {item.itemName}
                        </span>
                        {item.stock <= item.reorderLevel && (
                          <FiAlertTriangle
                            size={16}
                            className="ml-2 text-yellow-500"
                            title="Low stock"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {item.measurement}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-800 text-right font-semibold">
                      {item.stock}
                    </td>
                  <td className="px-6 py-4 text-sm text-gray-500 text-right">
  {showCurrencySymbol(Number(item.unitPrice || 0).toFixed(2))}
</td>

                    <td className="px-6 py-4 text-sm text-gray-500 text-right">
                      {item.reorderLevel}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setEditingItem(item)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <FaEdit size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-500">
                    No raw materials found. Add some to get started!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* 🔹 Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 gap-2">
          {[...Array(totalPages).keys()].map((num) => (
            <button
              key={num}
              onClick={() => setCurrentPage(num + 1)}
              className={`px-3 py-1 rounded-md border ${
                currentPage === num + 1
                  ? "bg-primary text-white"
                  : "bg-white text-gray-700"
              }`}
            >
              {num + 1}
            </button>
          ))}
        </div>
      )}

      {isAddModalOpen && (
        <AddBulkInventoryModal
          onClose={() => setIsAddModalOpen(false)}
          onSubmit={handleAddNewItems}
        />
      )}
      {editingItem && (
        <InventoryFormModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSubmit={handleUpdateItem}
        />
      )}
    </div>
  );
};

export default RawInventoryList;
